import { BaseText, BottomButtonsLayout, Button, Spinner } from 'aimo-ui';
import ApproveTermsSelectCountry from 'components/approveTerms/ApproveTermsSelectCountry';
import ApproveTermsText from 'components/approveTerms/ApproveTermsText';
import AccountContext from 'context/AccountContext';
import useBottomMenuHeight from 'hooks/useBottomMenuHeight';
import useHandleCustomerMutation from 'hooks/useHandleCustomerMutation';
import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { featureCountryCode } from 'states/persistInStorage';
import {
  CenterFullScreen,
  ScreenContainer,
  ScreenWrapper,
  StyledWrapper,
} from 'styles/ContainerStyles';
import { CountryCode } from 'types/generatedSchemaTypes';
import { isWeb } from 'utils/deviceUtils';
import Logger from 'utils/Logger';
import { testIdentifiers } from 'utils/testIdentifiers';
import { ToggleRowItem } from '../account/ViewTerms';

const contentContainerStyle = { flexGrow: 1 };
/**
 * Screen for approving terms and conditions after sign up or sign in
 */
const ApproveTerms = () => {
  const { t } = useTranslation();
  const {
    customer: { countryCode, accountType },
    loading: loadingCustomer,
  } = useContext(AccountContext);
  const tabBarHeight = useBottomMenuHeight();
  const { handleAppCustomer, loading } = useHandleCustomerMutation();
  const [privacyAndTerms, setPrivacyAndTerms] = useState(false);
  const [useOfVehicle, setUseOfVehicle] = useState(false);
  const [ageVerification, setAgeVerification] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(
    countryCode ?? undefined
  );

  const dropdownDisabled = useMemo(() => {
    return !!(
      countryCode &&
      (accountType === 'EXISTING_TP' ||
        accountType === 'INVITED_AIMO' ||
        accountType === 'EXISTING_AIMO')
    );
  }, []);

  const handleContinue = async () => {
    try {
      featureCountryCode(selectedCountry);
      await handleAppCustomer(marketingConsent, selectedCountry as CountryCode);
    } catch (error) {
      Logger.error(`Handle continue | Error: ${error}`);
    }
  };

  return (
    <ScreenContainer mb={1}>
      {loadingCustomer ? (
        <CenterFullScreen>
          <Spinner />
        </CenterFullScreen>
      ) : (
        <ScreenWrapper
          contentContainerStyle={contentContainerStyle}
          tabBarHeight={tabBarHeight}>
          <BottomButtonsLayout
            scrollable={true}
            contentBottom={
              <StyledWrapper center={isWeb} maxWidth={isWeb ? 450 : undefined}>
                <Button
                  {...testIdentifiers('approve-terms-continue')}
                  size="l"
                  buttonType="secondary"
                  text={t('button.continue')}
                  disabled={
                    !(
                      privacyAndTerms &&
                      useOfVehicle &&
                      ageVerification &&
                      selectedCountry
                    ) || loading
                  }
                  onPress={handleContinue}
                  loading={loading}
                />
              </StyledWrapper>
            }>
            <StyledWrapper maxWidth={isWeb ? undefined : 350} center>
              <StyledWrapper mb={20}>
                <BaseText>{t('account.terms.approveTermsInfo')}</BaseText>
              </StyledWrapper>
              <ApproveTermsSelectCountry
                country={selectedCountry}
                setSelectedCountry={setSelectedCountry}
                disabled={dropdownDisabled}
              />
              <StyledWrapper mb={10}>
                <ToggleRowItem
                  title="account.terms.privacyPolicyAndTermsConditions"
                  infoComponent={<ApproveTermsText />}
                  value={privacyAndTerms}
                  setValue={setPrivacyAndTerms}
                  testID={'privacy-terms-toggle'}
                  disabled={!selectedCountry}
                />
                <ToggleRowItem
                  title="account.terms.useOfVehicle"
                  info="account.terms.useOfVehicleInfo"
                  value={useOfVehicle}
                  setValue={setUseOfVehicle}
                  testID={'use-of-vehicle-toggle'}
                  disabled={!selectedCountry}
                />
                <ToggleRowItem
                  title="account.terms.ageVerification"
                  info="account.terms.ageVerificationInfo"
                  value={ageVerification}
                  setValue={setAgeVerification}
                  testID="age-verification-toggle"
                  disabled={!selectedCountry}
                />
                <ToggleRowItem
                  title="account.terms.marketingConsentOptional"
                  info="account.terms.marketingConsentOptionalInfo"
                  value={marketingConsent}
                  setValue={setMarketingConsent}
                  separator={false}
                  testID="marketing-consent-toggle"
                />
              </StyledWrapper>
            </StyledWrapper>
          </BottomButtonsLayout>
        </ScreenWrapper>
      )}
    </ScreenContainer>
  );
};

export default ApproveTerms;
